import React, { createContext, Dispatch, SetStateAction, useContext } from "react";
import { useEffect, useState } from "react";
import { IntlProvider, useIntl } from "react-intl";
import { getLocalePrimaryTag } from "../tools";

const APP_NAME_ID = "app_name" as const;
const APP_DESCRIPTION_ID = "app_description" as const;

const TitleTranslator = ({ children }) => {
    const intl = useIntl();

    useEffect(() => {
        if (intl.messages) {
            const appName = intl.formatMessage({ id: "app_name", defaultMessage: "Heimdall" });
            const appDescription = intl.formatMessage({ id: "app_description", defaultMessage: "Deverto's authorization application" });

            // only set the title if the translations are properly loaded
            // and it does not fallback to the ids
            if (appName !== APP_NAME_ID && appDescription !== APP_DESCRIPTION_ID) {
                document.title = `${appName} - ${appDescription}`;
            }
        }
    }, [intl]);

    return children;
};

/** Simple context to provide a way for children to change the user locale */
export type TranslationsData = {
    userLocale?: string;
    setUserLocale: Dispatch<SetStateAction<string>>;
};

const TranslationsDataContext = createContext<TranslationsData>(undefined);

export function useTranslationsData(): TranslationsData {
    return useContext(TranslationsDataContext);
}

const TranslationsProvider = ({ children }) => {
    const [userLocale, setUserLocale] = useState(getLocalePrimaryTag(navigator.language));
    const [translations, setTranslations] = useState(undefined);

    useEffect(() => {
        fetch(`/api/translations/${userLocale}`)
            .then((response) => response.json().then(setTranslations).catch(console.error))
            .catch(console.error);
    }, [userLocale]);

    return (
        <TranslationsDataContext.Provider value={{ userLocale, setUserLocale }}>
            <IntlProvider locale={userLocale} messages={translations} fallbackOnEmptyString={false}>
                <TitleTranslator>{children}</TitleTranslator>
            </IntlProvider>
        </TranslationsDataContext.Provider>
    );
};

export default TranslationsProvider;
